import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, filter, finalize, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

import { LoadingService } from 'src/app/shared/services/loading.service';
import { environment } from 'src/environments/environment';

interface IdentityUser {
  id: string;
  username: string;
  role: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private interactionInProgress: boolean = false;
  private currentUser: AccountInfo | null = null;

  private isAuthenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private userSubject = new BehaviorSubject<IdentityUser | null>(null);
  user$ = this.userSubject.asObservable();

  private userRoleSubject = new BehaviorSubject<string | null>(null);
  userRole$ = this.userRoleSubject.asObservable();

  constructor(
    private http: HttpClient,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private loadingService: LoadingService
  ) {
    this.initializeUser();
    this.monitorLoginChanges();
  }

  login() {
    if (this.interactionInProgress) {
      console.warn('Login already in progress');
      return;
    }

    this.interactionInProgress = true;

    const loginRequest: RedirectRequest = {
      scopes: [environment.azure.scope],
      redirectStartPage: '/'
    };

    this.msalService.loginRedirect(loginRequest);
  }

  logout() {
    this.msalService.logoutRedirect();
  }

  /** Get the current user */
  getUser(): IdentityUser | null {
    return this.userSubject.value;
  }

  /** Check if user has a specific role */
  hasRole(role: string): boolean {
    return this.userSubject.value?.role === role || false;
  }

  isAuthenticated() {
    return !!this.msalService.instance.getActiveAccount();
  }

  /** Fetch the ASP.NET Identity user info */
  fetchUserInfo(): Observable<IdentityUser> {
    this.loadingService.show();

    return this.http.get<IdentityUser>('/api/auth/me').pipe(
      tap(user => {
        this.userSubject.next(user);
        this.userRoleSubject.next(user.role ?? null);
      }),
      finalize(() => this.loadingService.hide()),
      shareReplay(1),
    );
  }

  getActiveAccount(): AccountInfo | null {
    return this.currentUser;
  }

  /** Runs on startup to check if user is already logged in */
  private initializeUser() {
    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      this.fetchUserInfo().subscribe();
    }
  }

  private checkUserAuthentication(): Observable<boolean> {
    const account = this.msalService.instance.getActiveAccount();

    if (account) {
      // If an active account is found, the user is authenticated
      this.isAuthenticatedSubject.next(true);
      return of(true);
    } else {
      // If no active account is found, the user is not authenticated
      this.isAuthenticatedSubject.next(false);
      return of(false);
    }
  }

  /** Listens for new login events and fetches user info */
  private monitorLoginChanges() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((event: any) => event.eventType === 'msal:loginSuccess'),
        switchMap(() => this.fetchUserInfo())
      )
      .subscribe();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((event: any) => (event.eventType === 'msal:loginSuccess' || event.eventType === 'msal:logoutSuccess')),
        switchMap(() => this.checkUserAuthentication())
      )
      .subscribe();
  }
}
